import React, { Component, forwardRef, useImperativeHandle, useRef } from "react";
import * as PropTypes from "prop-types";
import ChallengeDialog from "../../ChallengeDialog";
import CircularCountDown from "../../base/CircularCountDown";
// Rimuovi import CircularProgressbar (non più necessario)
// import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
// import 'react-circular-progressbar/dist/styles.css';
import ChallengeContext from '../../../../store/challenge-context';
import { useResetChallenge } from '../../../../hooks/useResetChallenge';
import { useEvaluateStep } from '../../../../hooks/useEvaluateStep';
import { useFinishChallenge } from '../../../../hooks/useFinishChallenge';
import { withStyles } from '@material-ui/core/styles';
import { useNavigate } from 'react-router-dom';

// Aggiungi gli stili CSS con logica dinamica
const styles = theme => ({
  timerPosition: {
    width: 110,
    height: 110,
    position: 'absolute',
    top: -27,
    left: -134,
  },
  // Stili per quando hasMedia è true
  timerPositionWithMedia: {
    width: 110,
    height: 110,
    position: 'absolute',
    top: -27,
    left: -134,
    [theme.breakpoints.down(1600)]: {
      top: -115,
      left: 25,
    },
    [theme.breakpoints.down(1500)]: {
      top: -115,
      left: 25,
    },
    // Su mobile: riduci dimensioni del 25%
    [theme.breakpoints.down('sm')]: {
      position: 'relative',
      top: 0,
      left: 0,
      width: 60,
      height: 60,
      margin: '0 0 24px 0',
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'flex-start'
    }
  },
  // Stili per quando hasMedia è false
  timerPositionWithoutMedia: {
    width: 110,
    height: 110,
    position: 'absolute',
    top: -27,
    left: -134,
    [theme.breakpoints.down(1600)]: {
      top: -27,
      left: -134,
    },
    [theme.breakpoints.down(1500)]: {
      top: -115,
      left: 25,
    },
    // Su mobile: riduci dimensioni del 25%
    [theme.breakpoints.down('sm')]: {
      position: 'relative',
      top: 0,
      left: 0,
      width: 60,
      height: 60,
      margin: '0 0 24px 0',
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'flex-start'
    }
  },
  // Nuovo stile per il container del timer testuale
  timerTextContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    
  },
  // Stile per il label "Current Time"
  timerLabel: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 600,
    color: theme.palette.common.gamsPetroleumBlue,
    textAlign: 'center',
    lineHeight: 1,
    marginBottom: theme.typography.pxToRem(6),
    [theme.breakpoints.down('sm')]: {
      fontSize: theme.typography.pxToRem(12), // Più piccolo su mobile
    }
  },
  // Stile per il tempo
  timerTime: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 700,
    color: theme.palette.common.gamsPetroleumBlue,
    textAlign: 'center',
    lineHeight: 1,
    [theme.breakpoints.down('sm')]: {
      fontSize: theme.typography.pxToRem(12), // Più piccolo su mobile
    }
  },
  // Nuovo stile per il container principale
  challengeInfoContainer: {
    display: 'inline-block',
    // Su mobile: larghezza piena per il bottone Info Challenge
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      // Rimuovi marginBottom da qui, lo gestiremo nel leftColumn
    }
  },
  
  // Modifica per il container del ChallengeDialog
  challengeDialogContainer: {
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      '& button': { // Stila il bottone Info Challenge
        width: '100%',
        height: '48px', // Stessa altezza dei bottoni large
        fontSize: '0.9375rem', // Stessa dimensione font dei bottoni large
        justifyContent: 'center',
        minWidth: 'auto', // Rimuovi minWidth per permettere larghezza piena
      }
    }
  }
});

// Wrapper function component per usare gli hooks
const ChallengeInfoWithHook = forwardRef((props, ref) => {
  const resetChallengeMutation = useResetChallenge();
  const evaluateStepMutation = useEvaluateStep();
  const finishChallengeMutation = useFinishChallenge();
  const navigate = useNavigate();
  const challengeInfoInstance = useRef();
  
  useImperativeHandle(ref, () => ({
    getStepTimeInSeconds: () => {
      return challengeInfoInstance.current?.getStepTimeInSeconds() || 0;
    }
  }));
  
  return (
    <ChallengeInfoClass 
      {...props}
      ref={challengeInfoInstance}
      resetChallengeMutation={resetChallengeMutation}
      evaluateStepMutation={evaluateStepMutation}
      finishChallengeMutation={finishChallengeMutation}
      navigate={navigate}
    />
  );
});

class ChallengeInfoClass extends Component {
  static contextType = ChallengeContext;

  constructor(props) {
    super(props);
    this.state = { 
      ...props, 
      open: false, 
      key: 0,
      elapsedTime: 0,      // Timer visibile per cronometro progressivo (secondi)
      stepTime: 0          // Timer sottobanco per lo step (SOLO secondi)
    };
    this.handleExit = this.handleExit.bind(this);
    this.handleFinish = this.handleFinish.bind(this);
    this.handleSkip = this.handleSkip.bind(this);
    this.timerReset = React.createRef();
    this.timerSet = React.createRef();
    this.timerStart = React.createRef();
    this.incrementalTimer = null;     // Timer visibile (cronometro progressivo)
    this.stepTimer = null;            // Timer sottobanco (solo secondi)
  }

  componentDidMount() {
    // SEMPRE avvia il timer sottobanco per tracciare il tempo dello step
    this.startStepTimer();
    
    // Avvia il timer incrementale visibile SOLO se timerEnabled è true
    if (this.props.timerEnabled) {
      this.startIncrementalTimer();
    }
  }

  componentDidUpdate(prevProps) {
    if(prevProps.step !== this.props.step) {
      // Reset di tutti i timer al cambio step
      this.setState({
        key: this.state.key + 1, 
        elapsedTime: 0,
        stepTime: 0          // Reset timer sottobanco
      });
    
      // SEMPRE riavvia il timer sottobanco
      this.startStepTimer();
      
      // Riavvia il timer visibile SOLO se timerEnabled è true
      if (this.props.timerEnabled) {
        this.startIncrementalTimer();
      }
    }
  }

  componentWillUnmount() {
    // Pulisci entrambi i timer quando il componente viene smontato
    if (this.incrementalTimer) {
      clearInterval(this.incrementalTimer);
    }
    if (this.stepTimer) {
      clearInterval(this.stepTimer);
    }
  }

  // Timer sottobanco che traccia SOLO secondi
  startStepTimer = () => {
    // Pulisci timer sottobanco esistente
    if (this.stepTimer) {
      clearInterval(this.stepTimer);
    }

    this.setState({ stepTime: 0 });

    // Timer sottobanco che aggiorna ogni secondo
    this.stepTimer = setInterval(() => {
      this.setState(prevState => ({
        stepTime: prevState.stepTime + 1
      }));
    }, 1000); // Aggiorna ogni secondo
  };

  // Timer visibile per cronometro progressivo
  startIncrementalTimer = () => {
    // Pulisci timer visibile esistente
    if (this.incrementalTimer) {
      clearInterval(this.incrementalTimer);
    }

    this.setState({ elapsedTime: 0 });

    // Timer visibile che aggiorna ogni secondo
    this.incrementalTimer = setInterval(() => {
      this.setState(prevState => ({
        elapsedTime: prevState.elapsedTime + 1
      }));
    }, 1000); 
  };

  // Funzione per ottenere il tempo dello step in secondi (sottobanco)
  getStepTimeInSeconds = () => {
    return this.state.stepTime;
  };

  // Funzione per ottenere il tempo dello step formattato in MM:SS (sottobanco)
  getFormattedStepTime = () => {
    const mins = Math.floor(this.state.stepTime / 60);
    const secs = this.state.stepTime % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Funzione per esporre il tempo dello step al componente padre
  getStepTime = () => {
    return {
      timeSeconds: this.getStepTimeInSeconds(),
      timeFormatted: this.getFormattedStepTime(),
      minutes: Math.floor(this.state.stepTime / 60),
      seconds: this.state.stepTime % 60
    };
  };

  // Modifica le funzioni esistenti per passare il tempo dello step 
  handleSkip = async () => {
    var id;
    const stepTimeSeconds = this.getStepTimeInSeconds(); // Ottieni tempo dello step in secondi
    const action = {
      actionNeeded: "click",
      target: "button",
      parameter: "skip",
      validationField: "skip",
      stepTime: stepTimeSeconds  // Tempo dello step in secondi 
    };
    
    if (window.location.pathname.split("/").pop()) {
        id = window.location.pathname.split("/").pop();
      if (
        window.confirm(
          "Are you sure you want to SKIP the step of the Challenge? Your score will be 0"
        )
      ) {
        try {
         
          const result = await this.props.evaluateStepMutation.mutateAsync({
            challengeId: id,
            action: action
          });
          
          const targetUrl = `${result.challengeStepEvaluator.targetLocation}${id}`;

        // Naviga usando React Router v6
          window.location.assign(targetUrl);
          // const skipStep = this.props.skip;
          
          
        } catch (error) {
          console.log(error);
        }
      }
    } else {
      this.setState({ open: false });
    }
  };

  handleFinish = async () => {
    var id;
    const stepTimeSeconds = this.getStepTimeInSeconds(); // Ottieni tempo dello step in secondi
    var key = window.location.pathname.split("/").pop() || '';
      if (
        window.confirm(
          "Are you sure you want to FINISH the Challenge? Your progress will be saved only until this step and so it will be your scoring"
        )
      ) {
        try {
          await this.props.finishChallengeMutation.mutateAsync({
            challengeId: key,
            stepTime: stepTimeSeconds  // Tempo dello step in secondi (sottobanco)
          });
          this.props.history.push("/challenge/end/" + key);
        } catch (error) {
          console.log(error);
        }
      }
     else {
      this.setState({ open: false });
    }
  };

  resetTimer(reset) {
    reset();
  }

  handleExit = async () => {
    var id;
    const pathSegments = window.location.pathname.split("/").filter(Boolean);
    if (pathSegments.length >= 2) {
      id = pathSegments[pathSegments.length - 2]; 
      if (
        window.confirm(
          "Are you sure you want to exit the Challenge? Your record won't be saved and you will need to retake the challenge"
        )
      ) {
        try {
          await this.props.resetChallengeMutation.mutateAsync({
            challengeType: id
          });
          
          this.forceUpdate();
          
          if (this.context.challengeOrigineRoute) {
            this.props.history.push(this.context.challengeOrigineRoute);
          } else {
            this.props.history.goBack();
          }
        } catch (error) {
          console.log(error);
        }
      }
    } else {
      this.setState({ open: false });
    }
  };

  render() {
    const { classes } = this.props;
    
    // Formatta il tempo per il display (timer visibile - solo minuti e secondi)
    const formatTime = (seconds) => {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Scegli la classe CSS corretta basata su hasMedia
    const timerClass = this.props.hasMedia ? 
      classes.timerPositionWithMedia : 
      classes.timerPositionWithoutMedia;

    return (
      <div className={classes.challengeInfoContainer}>
        {this.props.timerEnabled ? (
          // Timer incrementale visibile - SOLO se timerEnabled è true
          <div className={timerClass}>
            <div className={classes.timerTextContainer}>
              <div className={classes.timerLabel}>Step Time</div>
              <div className={classes.timerTime}>
                {formatTime(this.state.elapsedTime)}
              </div>
            </div>
          </div>
        ) : (
          // Timer decrementale con CircularCountDown SOLO SE time esiste ed è > 0
          this.props.time && this.props.time > 0 && (
            <CircularCountDown
              time={this.props.time} 
              step={this.state.key}
              challengeInfo={true}
              hasMedia={this.props.hasMedia} 
            />
          )
        )}
        
        <div className={classes.challengeDialogContainer}>
          <ChallengeDialog
            //title="Info Challenge"
            handleExit={this.handleExit}
            handleFinish={this.handleFinish}
            handleSkip={this.handleSkip}
            undoName="Resume"
            step={this.props.step}
            challengeInfo={true}
            brief={this.props.description}
            coach={this.props.coach}
            challengeTitle={this.props.challengeTitle}
            challengeIsValidation={this.props.challengeIsValidation}
            challengeDifficulty={this.props.challengeDifficulty}
            challengeAverageTime={this.props.challengeAverageTime}
          />
        </div>
      </div>
    );
  }
}

ChallengeInfoClass.propTypes = {
  time: PropTypes.any,
  description: PropTypes.any,
  handleEnd: PropTypes.any,
  coach: PropTypes.any,
  challengeTitle: PropTypes.any,
  challengeIsValidation: PropTypes.any,
  challengeDifficulty: PropTypes.any,
  challengeAverageTime: PropTypes.any,
  resetChallengeMutation: PropTypes.object.isRequired,
  evaluateStepMutation: PropTypes.object.isRequired,
  finishChallengeMutation: PropTypes.object.isRequired,
  timerEnabled: PropTypes.bool,
  blockedSteps: PropTypes.array,
  classes: PropTypes.object.isRequired,
  onStepTimeChange: PropTypes.func, // NUOVO: Callback per notificare cambi di tempo
};

export default withStyles(styles)(ChallengeInfoWithHook);
